import { useEffect, useId, useState } from 'react';
const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5008';

export function ManufacturerFilter({ selected, onChange, defaultSelectedCount = 0 }) {
  const [fabricantes, setFabricantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const filterId = useId();

  useEffect(() => {
    const controller = new AbortController();
    async function carregar() {
      try {
        const token = localStorage.getItem('@user_token');
        if (!token) throw new Error('Sessão expirada. Faça login novamente.');
        const response = await fetch(`${baseUrl}/api/Fabricante`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal
        });
        if (response.status === 401) throw new Error('Sessão expirada. Faça login novamente.');
        if (response.status === 403) throw new Error('Você não tem permissão para consultar fabricantes.');
        if (response.status === 429) throw new Error('Muitas consultas. Tente novamente em instantes.');
        if (!response.ok) throw new Error('Não foi possível carregar os fabricantes.');
        const items = await response.json();
        if (!Array.isArray(items)) throw new Error('Resposta inválida ao consultar fabricantes.');
        if (controller.signal.aborted) return;
        setFabricantes(items);
        if (defaultSelectedCount > 0) onChange(items.slice(0, defaultSelectedCount).map(item => item.codigo));
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    carregar();
    return () => controller.abort();
  }, [onChange, defaultSelectedCount]);

  const normalizedSearch = search.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const visible = fabricantes.filter(item => String(item.nome ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(normalizedSearch));
  const toggle = codigo => onChange(selected.includes(codigo) ? selected.filter(item => item !== codigo) : [...selected, codigo]);

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">Fabricante</span>
      <button type="button" aria-label="Filtro de fabricantes" aria-expanded={expanded} aria-controls={filterId} onClick={() => setExpanded(value => !value)} className="flex min-h-9 w-full items-center justify-between rounded-md border border-slate-800 bg-slate-900 px-2 py-2 text-left text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
        <span>{selected.length === 0 ? 'Todos os fabricantes' : `${selected.length} fabricante(s) selecionado(s)`}</span>
        <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
      </button>
      {!expanded && error && <p role="alert" className="text-xs text-red-300">{error}</p>}
      {expanded && <fieldset id={filterId} className="flex min-w-0 flex-col gap-2" disabled={loading || Boolean(error)}>
      <legend className="sr-only">Selecionar fabricantes</legend>
      {loading && <p role="status" className="text-xs text-slate-400">Carregando fabricantes...</p>}
      {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
      {!loading && !error && fabricantes.length === 0 && <p className="text-xs text-slate-400">Nenhum fabricante cadastrado.</p>}
      {!loading && !error && fabricantes.length > 0 && <>
        <input type="search" aria-label="Buscar fabricante" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar fabricante" className="h-9 w-full rounded-md border border-slate-800 bg-slate-900 px-2 text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="flex items-center gap-2 text-xs text-slate-200">
          <input type="checkbox" checked={selected.length === 0} onChange={() => onChange([])} className="accent-blue-500 focus-visible:outline-2 focus-visible:outline-blue-500" />
          Todos os fabricantes
        </label>
        <div className="max-h-40 overflow-y-auto rounded-md border border-slate-800 p-2">
          {visible.map(item => <label key={item.codigo} className="flex items-start gap-2 py-1.5 text-xs text-slate-200">
            <input type="checkbox" checked={selected.includes(item.codigo)} onChange={() => toggle(item.codigo)} className="mt-0.5 shrink-0 accent-blue-500 focus-visible:outline-2 focus-visible:outline-blue-500" />
            <span className="break-words">{item.nome?.trim() || `Fabricante ${item.codigo} (nome não informado)`}</span>
          </label>)}
          {visible.length === 0 && <p className="text-xs text-slate-400">Nenhum fabricante encontrado.</p>}
        </div>
        <p className="text-[11px] text-slate-400">{selected.length === 0 ? 'Todos os fabricantes' : `${selected.length} fabricante(s) selecionado(s)`}</p>
      </>}
      </fieldset>}
    </div>
  );
}
